import { motion } from 'framer-motion';
import { User, Grid2x2, FileBarChart, CalendarClock, TimerReset, Edit3 } from 'lucide-react';

/**
 * CloudHub Empathise-phase diagrams, native React re-renders of the
 * User Journey Map and Empathy Map artefacts from the source case study.
 *
 * Rebuilt so they inherit the site's theme tokens, scale crisply at any DPI,
 * and stagger-animate on scroll, instead of shipping as PNG screenshots.
 */

const easeOut = [0.2, 0.8, 0.2, 1];

// ── User Journey Map ─────────────────────────────────────────────────────
// 6 steps × 3 rows (Needs & Goal / Challenges / Solution)

const journeySteps = [
    {
        Icon: User,
        title: 'Login',
        goals: ['Securely access the cloud hub and manage administrative tasks.'],
        challenges: ['Remembering login credentials, potential multi-factor authentication delays.'],
        solutions: [
            'Implement single sign-on (SSO) where possible.',
            'Offer the option to remember login credentials securely.',
        ],
    },
    {
        Icon: Grid2x2,
        title: 'Scheduler Overview',
        goals: [
            'View a brief summary of the statistics and status of the inventory.',
            'Verify program and server disk space information.',
            'Check the statistics for the scheduler.',
        ],
        challenges: [
            'No overview area to view main key highlights of the current website.',
            'Complex cloud data isn\u2019t visualised effectively, hard to gain insights into utilisation and performance.',
        ],
        solutions: [
            'Provide an overview design that assists the user in obtaining a summary of the main points.',
        ],
    },
    {
        Icon: FileBarChart,
        title: 'Inventory Statistics',
        goals: [
            'Check server running status and take action to manage the scheduler.',
            'View particular program and server details.',
        ],
        challenges: [
            'Difficult to find program hierarchy details.',
            'The UI causes too much confusion for users to complete specific tasks.',
            'Proper filter functionality is not available.',
        ],
        solutions: [
            'Inventory statistics let users obtain proper program hierarchy information and take action on individual program schedules.',
        ],
    },
    {
        Icon: CalendarClock,
        title: 'Program Schedule View',
        goals: [
            'The scheduler manages jobs across servers in various regions.',
            'View scheduler program details and manage the scheduler.',
            'Take action on the scheduler at the individual program level.',
        ],
        challenges: [
            'Accessing details at both program and server levels is missing.',
            'Interface can be complex or lack intuitiveness, making it hard to navigate and find the information users need.',
        ],
        solutions: [
            'Via the program scheduler, users access accurate program hierarchy information and manage individual program schedules.',
            'Corrected content hierarchy and improved UI make it easier to accomplish tasks.',
        ],
    },
    {
        Icon: TimerReset,
        title: 'Create New Schedule',
        goals: [
            'Create a scheduled task at the program level.',
            'Identify specific programs, environments, and production info via appropriate filter actions.',
        ],
        challenges: [
            'Identifying program and environment levels is difficult due to lack of UX/UI functionality and inconsistent visual style across functionalities.',
        ],
        solutions: [
            'Improved UX and UI enables users to create new schedules, making it easier to complete tasks.',
        ],
    },
    {
        Icon: Edit3,
        title: 'Manage Schedule',
        goals: [
            'Modify the scheduler\u2019s timing and days.',
            'Switch the scheduler on and off at the particular program level easily.',
        ],
        challenges: [
            'The UI causes too much confusion for users to complete managing the schedules.',
        ],
        solutions: [
            'Improved UX and UI enables users to modify the scheduler and complete tasks with less friction.',
        ],
    },
];

const rowMeta = [
    { key: 'goals',     label: 'Needs & Goal', accent: '#60a5fa', tint: 'rgba(96, 165, 250, 0.10)' },
    { key: 'challenges', label: 'Challenges',  accent: '#f472b6', tint: 'rgba(244, 114, 182, 0.10)' },
    { key: 'solutions', label: 'Solution',    accent: '#34d399', tint: 'rgba(52, 211, 153, 0.10)' },
];

export const CloudHubJourneyMap = () => (
    <motion.figure
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-6%' }}
        transition={{ duration: 0.7, ease: easeOut }}
        className="mb-6 md:mb-8 rounded-2xl border border-[var(--line)] bg-[var(--surface)] p-5 md:p-7"
        data-testid="cloudhub-journey-map"
    >
        <header className="mb-6 md:mb-8">
            <div className="font-mono-brand text-[10.5px] uppercase tracking-[0.28em] text-[var(--accent)]">
                CloudHub Scheduler · Journey Steps
            </div>
            <h4 className="mt-2 font-display text-3xl md:text-4xl text-[var(--ink)]">
                User journey map<span className="text-[var(--accent)]">.</span>
            </h4>
            <p className="mt-2 font-sans-brand text-[13.5px] md:text-[14.5px] leading-[1.6] text-[var(--ink-2)]">
                <span className="text-[var(--ink)] font-medium">CloudHub Scheduler</span>{' '}
                empowers authorized users to plan and schedule maintenance or downtime
                windows for your applications and services, minimizing disruptions and
                optimizing operational efficiency.
                <br />
                <br />
                This cloud tool is tailored to meet specific needs, with the Scheduler
                managing jobs across servers in different regions. Internal screens include
                Inventory for a comprehensive server overview by Account/Program and
                Scheduler for managing scheduled servers, creating new schedules, and
                Program mapping.
            </p>
        </header>

        {/* MOBILE (< md), stacked cards, one per step, with 3 tinted sections */}
        <div className="md:hidden flex flex-col gap-4">
            {journeySteps.map(({ Icon, title, goals, challenges, solutions }, i) => {
                const step = { goals, challenges, solutions };
                return (
                    <motion.article
                        key={i}
                        initial={{ opacity: 0, y: 12 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: '-4%' }}
                        transition={{ delay: 0.1 + i * 0.04, duration: 0.45 }}
                        className="rounded-xl border border-[var(--line)] overflow-hidden"
                    >
                        <header className="flex items-center gap-3 p-3.5 bg-[var(--surface-2)]">
                            <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-blue-500/15 text-blue-300 shrink-0">
                                <Icon size={16} strokeWidth={2} />
                            </span>
                            <div className="flex-1 min-w-0">
                                <div className="font-mono-brand text-[9.5px] uppercase tracking-[0.22em] text-[var(--ink-3)]">
                                    Step {String(i + 1).padStart(2, '0')}
                                </div>
                                <div className="font-display text-[17px] leading-tight text-[var(--ink)] truncate">
                                    {title}
                                </div>
                            </div>
                        </header>
                        <div>
                            {rowMeta.map((row) => (
                                <div
                                    key={row.key}
                                    className="px-3.5 py-3 border-t border-[var(--line)]"
                                    style={{ background: row.tint }}
                                >
                                    <span
                                        className="inline-block rounded-md px-2 py-0.5 font-mono-brand text-[9.5px] uppercase tracking-[0.18em] mb-2"
                                        style={{
                                            color: row.accent,
                                            border: `1px solid ${row.accent}55`,
                                        }}
                                    >
                                        {row.label}
                                    </span>
                                    <ul className="flex flex-col gap-1.5">
                                        {step[row.key].map((item, k) => (
                                            <li
                                                key={k}
                                                className="flex items-start gap-2 font-sans-brand text-[12.5px] leading-[1.55] text-[var(--ink-2)]"
                                            >
                                                <span
                                                    className="mt-[7px] w-1 h-1 rounded-full shrink-0"
                                                    style={{ background: row.accent }}
                                                />
                                                <span>{item}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            ))}
                        </div>
                    </motion.article>
                );
            })}
        </div>

        {/* DESKTOP (≥ md), full 6-column matrix */}
        <div className="hidden md:block overflow-x-auto -mx-2 px-2">
            <div className="min-w-[1000px]">
                {/* Step-icon strip along the top */}
                <div className="grid grid-cols-[132px_repeat(6,1fr)] gap-3 mb-3">
                    <div />
                    {journeySteps.map(({ Icon, title }, i) => (
                        <div key={i} className="flex flex-col items-center text-center gap-2">
                            <motion.span
                                className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-blue-500/15 text-blue-300"
                                initial={{ scale: 0.6, opacity: 0 }}
                                whileInView={{ scale: 1, opacity: 1 }}
                                viewport={{ once: true }}
                                transition={{ delay: 0.15 + i * 0.06, duration: 0.4 }}
                            >
                                <Icon size={20} strokeWidth={2} />
                            </motion.span>
                            <div className="font-display text-[15px] md:text-[16px] leading-tight text-[var(--ink)]">
                                {title}
                            </div>
                        </div>
                    ))}
                </div>

                {/* Rows: goals, challenges, solutions */}
                {rowMeta.map((row, ri) => (
                    <div
                        key={row.key}
                        className="grid grid-cols-[132px_repeat(6,1fr)] gap-3 border-t border-[var(--line)]"
                    >
                        <div
                            className="py-4 px-3 flex items-center justify-center"
                            style={{ background: row.tint }}
                        >
                            <span
                                className="rounded-md px-2.5 py-1 font-mono-brand text-[10px] uppercase tracking-[0.18em] whitespace-nowrap"
                                style={{ color: row.accent, background: 'transparent', border: `1px solid ${row.accent}44` }}
                            >
                                {row.label}
                            </span>
                        </div>
                        {journeySteps.map((step, si) => (
                            <motion.ul
                                key={si}
                                className="py-4 px-2 flex flex-col gap-2"
                                initial={{ opacity: 0, y: 8 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: '-4%' }}
                                transition={{ delay: 0.2 + ri * 0.08 + si * 0.03, duration: 0.4 }}
                            >
                                {step[row.key].map((item, k) => (
                                    <li
                                        key={k}
                                        className="flex items-start gap-2 font-sans-brand text-[12px] md:text-[12.5px] leading-[1.5] text-[var(--ink-2)]"
                                    >
                                        <span
                                            className="mt-[7px] w-1 h-1 rounded-full shrink-0"
                                            style={{ background: row.accent }}
                                        />
                                        <span>{item}</span>
                                    </li>
                                ))}
                            </motion.ul>
                        ))}
                    </div>
                ))}
            </div>
        </div>
        <figcaption className="mt-4 font-mono-brand text-[10.5px] uppercase tracking-[0.22em] text-[var(--ink-3)]">
            User Journey Map · CloudHub Scheduler, needs, challenges & solutions across six steps
        </figcaption>
    </motion.figure>
);

// ── Empathy Map ──────────────────────────────────────────────────────────
// 2×2 quadrants (Says / Thinks / Does / Feels) around the CloudHub Admin persona.

const empathy = {
    says: {
        label: 'Says',
        colour: '#5eead4',          // teal-300
        tint: 'rgba(94, 234, 212, 0.14)',
        border: 'rgba(94, 234, 212, 0.55)',
        items: [
            'Managing multiple user accounts is a hassle.',
            'I wish I could see cloud resources in real-time.',
            'Generating reports takes forever and they\u2019re hard to understand.',
            'The Scheduler manages jobs across servers in various regions.',
        ],
    },
    thinks: {
        label: 'Thinks',
        colour: '#fbbf24',          // amber-400
        tint: 'rgba(251, 191, 36, 0.14)',
        border: 'rgba(251, 191, 36, 0.55)',
        items: [
            'User management should be easier and more efficient.',
            'There\u2019s got to be a streamlined way to handle tickets and compliance.',
            'I\u2019m worried about a security breach but don\u2019t know how to prevent it.',
            'There must be an easier way to generate reports for my team.',
        ],
    },
    does: {
        label: 'Does',
        colour: '#86efac',          // green-300
        tint: 'rgba(134, 239, 172, 0.14)',
        border: 'rgba(134, 239, 172, 0.55)',
        items: [
            'Spends time manually creating and managing user accounts.',
            'Exports raw data and spends hours creating reports for stakeholders.',
            'Relies on guesswork to identify potential cloud resource issues.',
            'Manually monitors user activity, lacks tools to identify threats.',
        ],
    },
    feels: {
        label: 'Feels',
        colour: '#93c5fd',          // blue-300
        tint: 'rgba(147, 197, 253, 0.14)',
        border: 'rgba(147, 197, 253, 0.55)',
        items: [
            'Simplified user management with clear access control would be a relief.',
            'Efficient tools for tickets and compliance would save real time.',
            'Real-time insights into cloud health would build confidence.',
            'A self-service portal would remove daily friction.',
        ],
    },
};

// David persona portrait (uploaded earlier, referenced from `caseStudies.js`).
const DAVID_AVATAR = '/api/files/17c7b8e1-7ad9-4e0c-b517-5bd6598aa96a';

const CornerNote = ({ q, align }) => (
    <div className={`flex flex-col gap-3 ${align === 'right' ? 'items-end text-right' : 'items-start'}`}>
        <span className="font-display text-4xl md:text-5xl text-white/90 leading-none">
            {q.label}
        </span>
        <motion.article
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-4%' }}
            transition={{ duration: 0.55, ease: easeOut }}
            className="w-full max-w-[340px] rounded-2xl border p-4 md:p-5 backdrop-blur-sm shadow-[0_10px_30px_rgba(0,0,0,0.35)]"
            style={{
                background: 'rgba(30, 64, 175, 0.55)',
                borderColor: 'rgba(255, 255, 255, 0.22)',
            }}
        >
            <ul className="flex flex-col gap-1.5">
                {q.items.map((it, i) => (
                    <li
                        key={i}
                        className="flex items-start gap-2 font-sans-brand text-[12.5px] md:text-[13px] leading-[1.5] text-white/95 text-left"
                    >
                        <span className="mt-[7px] w-1 h-1 rounded-full bg-white/70 shrink-0" />
                        <span>{it}</span>
                    </li>
                ))}
            </ul>
        </motion.article>
    </div>
);

// A tiny animated paper plane decoration, nod to the reference image.
const PaperPlane = ({ className, rotate = 0 }) => (
    <svg
        viewBox="0 0 60 40"
        className={`absolute pointer-events-none ${className}`}
        aria-hidden
        style={{ transform: `rotate(${rotate}deg)` }}
    >
        <path
            d="M2 20 L58 4 L34 38 L28 24 Z"
            fill="rgba(255,255,255,0.85)"
            stroke="rgba(255,255,255,0.6)"
            strokeWidth="0.6"
        />
        <path d="M2 20 L28 24 L34 38" stroke="rgba(255,255,255,0.5)" strokeWidth="0.6" fill="none" />
        <path
            d="M2 20 C -20 30, -32 12, -50 22"
            stroke="rgba(255,255,255,0.28)"
            strokeWidth="0.9"
            strokeDasharray="2 3"
            fill="none"
        />
    </svg>
);

export const CloudHubEmpathyMap = () => (
    <motion.figure
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-6%' }}
        transition={{ duration: 0.7, ease: easeOut }}
        className="mb-6 md:mb-8 rounded-2xl border border-[var(--line)] overflow-hidden"
        data-testid="cloudhub-empathy-map"
        style={{
            background:
                'radial-gradient(120% 100% at 50% 40%, #1d3a8e 0%, #142a68 45%, #0d1e4d 80%, #091640 100%)',
        }}
    >
        <header className="p-5 md:p-7 pb-3">
            <div className="font-mono-brand text-[10.5px] uppercase tracking-[0.28em] text-cyan-200/90">
                CloudHub Administrator
            </div>
            <h4 className="mt-2 font-display text-3xl md:text-4xl text-white">
                Empathy map<span className="text-[var(--accent)]">.</span>
            </h4>
            <p className="mt-2 font-sans-brand text-[13.5px] md:text-[14.5px] leading-[1.6] text-white/70">
                What the CloudHub Admin says, thinks, does and feels, mapped from the 24
                interviews. The four corners around David surface the emotional arc and the
                workflow friction that shaped the redesign brief.
            </p>
        </header>

        <div className="relative p-6 md:p-10 pt-2 md:pt-4">
            {/* Faint blueprint grid texture */}
            <div
                aria-hidden
                className="absolute inset-0 opacity-[0.05] pointer-events-none"
                style={{
                    backgroundImage:
                        'linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)',
                    backgroundSize: '56px 56px',
                }}
            />

            {/* Decorative paper planes with dashed trails */}
            <PaperPlane className="top-4 -left-6 w-16 hidden md:block" rotate={-8} />
            <PaperPlane className="bottom-8 -right-4 w-14 hidden md:block" rotate={172} />

            {/* 3-row grid: TL / TR, persona, BL / BR */}
            <div className="relative grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-6 md:gap-8 items-center">
                <CornerNote q={empathy.says} align="left" />
                {/* Central persona, spans the middle column and both rows */}
                <div className="row-span-2 flex flex-col items-center gap-3 md:mx-4">
                    <div
                        className="relative w-32 h-32 md:w-40 md:h-40 rounded-full overflow-hidden border-[3px]"
                        style={{
                            borderColor: 'rgba(255,255,255,0.85)',
                            boxShadow:
                                '0 0 0 8px rgba(255,255,255,0.06), 0 20px 40px rgba(0,0,0,0.45)',
                        }}
                    >
                        <img
                            src={DAVID_AVATAR}
                            alt=""
                            className="absolute inset-0 w-full h-full object-cover"
                            loading="lazy"
                        />
                    </div>
                    <motion.div
                        initial={{ opacity: 0, y: 8, rotate: -2 }}
                        whileInView={{ opacity: 1, y: 0, rotate: -2 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.4, duration: 0.5 }}
                        className="relative px-5 py-2 rounded-lg font-display text-white text-lg md:text-xl whitespace-nowrap"
                        style={{
                            background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                            boxShadow: '0 6px 18px rgba(37, 99, 235, 0.4)',
                        }}
                    >
                        Meet David.
                        <span
                            aria-hidden
                            className="absolute -left-2 top-1/2 -translate-y-1/2 w-3 h-3 rounded-sm"
                            style={{ background: '#1e40af' }}
                        />
                        <span
                            aria-hidden
                            className="absolute -right-2 top-1/2 -translate-y-1/2 w-3 h-3 rounded-sm"
                            style={{ background: '#1e40af' }}
                        />
                    </motion.div>
                </div>
                <CornerNote q={empathy.thinks} align="right" />
                <CornerNote q={empathy.feels} align="left" />
                <CornerNote q={empathy.does} align="right" />
            </div>
        </div>
    </motion.figure>
);

export default CloudHubJourneyMap;
